import { useCallback, useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { YOUZU_API_BASE_URL } from "@/constants/api";
import { loadRoomVisualizeSDK } from "@/lib/room-visualize-sdk";
import { createAccessToken } from "@/services/token.service";

export interface RoomVisualizeElement extends HTMLElement {
  resolveToken?: () => Promise<string>;
}
interface RoomVisualizeSDKProps {
  catalogueId: string;
  sdkUrl: string;
}

export function RoomVisualizeSDK({ catalogueId, sdkUrl }: RoomVisualizeSDKProps) {
  const [loadedUrl, setLoadedUrl] = useState("");
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let active = true;
    setFailed(false);
    loadRoomVisualizeSDK(sdkUrl).then(() => {
      if (active) setLoadedUrl(sdkUrl);
    }).catch(() => {
      if (active) setFailed(true);
    });
    return () => { active = false; };
  }, [sdkUrl, attempt]);

  const bindWidget = useCallback((element: RoomVisualizeElement | null) => {
    if (!element) return;
    // The authenticated dashboard endpoint binds the tenant and catalogue.
    // Its wildcard tokens are single-use, so mint one for every SDK request.
    element.resolveToken = () => createAccessToken({ catalogueId });
  }, [catalogueId]);

  if (failed) return (
    <div role="alert" className="flex flex-col items-center gap-4 py-12 text-center">
      <p>Room Visualizer could not load. Please try again.</p>
      <Button variant="outline" onClick={() => setAttempt(value => value + 1)}>Try again</Button>
    </div>
  );
  if (loadedUrl !== sdkUrl) return (
    <div role="status" className="flex items-center justify-center gap-3 py-12 text-muted-foreground">
      <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
      Loading Room Visualizer…
    </div>
  );

  return (
    <youzu-room-visualize
      key={`${sdkUrl}:${catalogueId}`}
      ref={bindWidget}
      api-base-url={YOUZU_API_BASE_URL}
      catalogue-id={catalogueId}
      lang="en"
      currency="USD"
      className="w-full"
    />
  );
}
